<template>
    <div
        v-if="statusPattern == ERROR"
        class="w-full px-auto flex flex-col gap-2 items-center"
    >
        <div class="font-light leading-4 min-w-full text-xs text-center text-red-600 whitespace-pre-wrap" >
            <p class="mb-0 break-words">{{ errorLabel }}</p>
        </div>

        <button
            v-if="showEditButton"
            :disabled="isRetrying"
            @click="handleButtonClick"
            :class="[
                'bg-red-600 flex gap-2.5 h-8 items-center justify-center mx-auto px-4 py-2 rounded transition-colors',
                isRetrying ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-700',
            ]"
            type="button"
        >
            <span class="font-semibold leading-6 text-xs text-center text-white">
                {{ isRetrying ? '実行中...' : editButtonLabel }}
            </span>
        </button>
    </div>
    <!-- 文字起こし進捗/%表示 -->
    <div v-else-if="statusPattern == GENERATING_ARTICLE">
        <p class="leading-4 text-xs text-gray-400 w-full font-light mb-0">
            文字起こし進捗
        </p>
        <p class="leading-5 text-sm text-gray-700 w-full font-light mb-0">
            {{ props.video.transcription_progress_display }}
        </p>
    </div>
    <!-- 文字起こし進捗/%表示 -->
    <div v-else-if="statusPattern == GENERATED_ARTICLE">
        <p class="leading-5 text-sm w-full whitespace-pre-wrap font-light mb-0">
            {{ props.video.status_transcription_display }}
        </p>
        <div class="leading-4 text-xs text-gray-400 w-full whitespace-pre-wrap font-light">
            <p class="mb-0">{{ dateLabel }}</p>
            <p class="mb-0">{{ timeLabel }}</p>
        </div>
    </div>
    <!-- 時間表示なし -->
    <div v-else>
        <p class="font-light leading-5 text-sm text-center w-full whitespace-pre-wrap mb-0">
            {{ props.video.status_transcription_display }}
        </p>
    </div>
</template>

<script setup>
/*
| statusPattern | 記事ステータス | Video#status_transcription | Video#transcription_progress | ステータス列の文言 | 記事編集 | 削除列の文言 | 備考 |
|----|----|----|----|----|----|----|----|
| error | 動画ファイルのみ登録 | '' | '0' | 詳細情報の登録を促すエラー文言/動画情報の修正 | - | 削除 | |
| error | 記事生成時エラー | generating | '25' | エラー内容/動画情報の修正 | - | 削除 | |
| generating | 記事生成中 | generating | '25' | 文字起こし進捗/25% | - | 削除 |  |
| generated | 文字起こし完了 | generated | '100' | 文字起こし完了/完了時の日時 | 記事を作成 | 削除 |  |
| generated | 下書き | pre_publication | '100' | 下書き/最終更新日時 | 記事を編集 | 削除 |  |
| generated | 予約投稿 | scheduled | '100' | 予約投稿/公開予定日時 | 記事を編集 | 削除 | |
| generated | 公開 | published | '100' | 公開/公開日時 | 記事を編集 | 公開取消 | 公開待ちに変化後LDNに取り込まれたらpublishedに変化 |
| no-datetime | 公開待ち | publishing_queued | '100' | 公開待ち | - | - | 公開設定後にpublishing_queued |
| no-datetime | 編集反映待ち | republishing_queued | '100' | 編集反映待ち | 記事を編集 | 公開取消 | - |
| no-datetime | 削除待ち | deleting | '100' | 削除済み | - | - | 削除依頼時はdeleting |
*/

import { defineProps, defineEmits, computed, ref } from 'vue';
import axios from 'axios';
import store from '../store';

const ERROR = "error";
const NO_DATETIME = "no_datetime";
const GENERATING_ARTICLE = "generating_article";
const GENERATED_ARTICLE = "generated_article";

const props = defineProps({
    video: {
        type: Object,
        default: () => {}
    },
});

const emit = defineEmits(["add-video-info", "retry-generate", "retry-error"]);

const apiEndpoint = process.env.VUE_APP_API_ENDPOINT;

const isRetrying = ref(false);

const isPermissionError = computed(() => {
    return props.video.latest_error == "GEMINI_API_PERMISSION_ERROR";
});

const isProhibitedContentError = computed(() => {
    return props.video.latest_error == "GEMINI_PROHIBITED_CONTENT_ERROR";
});

const isInvalidArgumentError = computed(() => {
    return props.video.latest_error == "GEMINI_API_INVALID_ARGUMENT_ERROR";
});

const isRetryExceededError = computed(() => {
    return props.video.latest_error == "GEMINI_API_RETRY_EXCEEDED_ERROR";
});

const LDN_ERROR_CODES = ['IMAGE_DOWNLOAD_FAILED', 'MISSING_REQUIRED_FIELD', 'NORMALIZE_FAILED', 'DB_SAVE_FAILED', 'UNKNOWN'];

const isLdnImageError = computed(() => props.video.latest_error === 'IMAGE_DOWNLOAD_FAILED');

const isLdnError = computed(() => LDN_ERROR_CODES.includes(props.video.latest_error));

const errorLabel = computed(() => {
    if(isPermissionError.value) {
        return "公開動画でないため、記事生成をすることができませんでした。\n動画ファイルをアップロードしてください。"
    }
    if(isProhibitedContentError.value) {
        return "AIの利用ガイドラインに基づく制限により、処理できませんでした。\n動画ファイルをアップロードして再度お試しください。"
    }
    if(isInvalidArgumentError.value) {
        return "動画情報が不正です。\nYouTubeのURLや動画ファイルをご確認いただき、再度アップロードしなおしてください。"
    }
    if(isRetryExceededError.value) {
        return "一時的なエラーが続いたため記事生成を中止しました。\n時間を空けて再実行をお試しください。"
    }
    if(props.video.transcription_progress === '0') {
        return "動画の詳細情報が登録されていません"
    }
    // LDN 取込エラー（汎用 latest_error ハンドラより前に判定）
    if(isLdnImageError.value) {
        return "動画が非公開のため記事投稿に失敗しました。"
    }
    if(isLdnError.value) {
        return "一時的なエラーにより記事投稿に失敗しました。"
    }
    if(props.video.latest_error) {
        return `記事生成時にエラーが発生しました。サポートに問い合わせ下さい。${props.video.latest_error}`
    }
    return ""
});

const editButtonLabel = computed(() => {
    if(isPermissionError.value || isProhibitedContentError.value || isInvalidArgumentError.value) {
        return "動画のアップロード"
    }
    if(isRetryExceededError.value) {
        return "記事生成を再実行"
    }
    return "動画情報の修正"
});

// LDN 取込エラーは記事管理テーブルの「記事を編集」ボタンに誘導するため、ここではボタン非表示
const showEditButton = computed(() => !isLdnError.value);

const datetime = computed(() => {
    const video = props.video;
    const article = video.article;
    const status_transcription = props.video.status_transcription;

    //- 文字起こし完了したら、完了時の日時を表示
    if(status_transcription === 'generated') {
        return new Date(article.created_at);
    }
    //- 予約投稿ステータスの時、予約の日時を表示 Article.date_published
    if(status_transcription === 'scheduled') {
        return new Date(article.date_published);
    }
    //- 公開ステータスの時、公開日時を表示 Article.date_published
    if(status_transcription === 'published') {
        return new Date(article.date_published);
    }
    //- 下書きステータスの時、最終更新日を表示
    if(status_transcription === 'pre_publication') {
        return new Date(article.updated_at);
    }
    return new Date()
});

const dateLabel = computed(() => {
    const year = datetime.value.getFullYear();
    const month = String(datetime.value.getMonth() + 1).padStart(2, '0');
    const day = String(datetime.value.getDate()).padStart(2, '0');

    return `${year}/${month}/${day}`;
});

const timeLabel = computed(() => {
    const hours = String(datetime.value.getHours()).padStart(2, '0');
    const minutes = String(datetime.value.getMinutes()).padStart(2, '0');

    return `${hours}:${minutes}`;
});

const statusPattern = computed(() => {
    // ERROR 動画ファイルのみ登録
    if(props.video.transcription_progress === '0') {
        return ERROR
    }

    // ERROR 記事生成時エラー / LDN 取込エラー（latest_error に値があれば両方カバー）
    if(props.video.latest_error && Object.keys(props.video.latest_error).length > 0) {
        return ERROR
    }

    // 日付情報なし
    if(props.video.status_transcription === 'publishing_queued' || // 公開待ち
        props.video.status_transcription === 'republishing_queued' || // 編集反映待ち
        props.video.status_transcription === "deleting" // 削除
    ) {
        return NO_DATETIME
    }

    // 記事生成中
    if(props.video.transcription_progress == '25') {
        return GENERATING_ARTICLE
    }

    return GENERATED_ARTICLE
});

const handleEditClick = () => {
    emit("add-video-info", props.video);
};

const handleRetryClick = async () => {
    if (isRetrying.value) return;
    isRetrying.value = true;
    try {
        const accessToken = store.state.accessToken;
        await axios.post(
            `${apiEndpoint}/videos/${props.video.video_id}/retry/`,
            {},
            {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            }
        );
        // 親コンポーネントに動画リスト再取得を依頼
        emit('retry-generate', props.video);
    } catch (e) {
        console.error('記事生成リトライエラー:', e);
        // 失敗時は親にエラーを通知。ボタンも再有効化する
        emit('retry-error', e);
    } finally {
        isRetrying.value = false;
    }
};

const handleButtonClick = () => {
    if (isRetryExceededError.value) {
        handleRetryClick();
    } else {
        handleEditClick();
    }
};
</script>
